import React, { useEffect, useState } from 'react'
import { useTranslate, required, Form, useRecordContext } from 'react-admin'
import { NumberInput } from 'src/components/uiElements/fields/NumberInput'
import SaveIcon from '@mui/icons-material/Save'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Button,
  DialogActions,
  Box,
  Typography,
  Skeleton,
} from '@mui/material'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import LoadingButton from '@mui/lab/LoadingButton'
import { useCardId } from 'src/components/pages/sales/reservation/context/CardIdContext'
import { CheckboxLabels } from 'src/components/uiElements/fields/CheckboxLabels'
import { HasPermission, hasPermissionFunction } from 'src/helper/HasPermission'
import {
  VIEW_LIQUIDATION_RETAIL,
  VIEW_LIQUIDATION_SUPPLIER,
  VIEW_LIQUIDATION_WHOLESALER,
  PERMISSION_SMARTBOOK,
  ITEM_PERMISSION_ENTITY,
} from 'src/helper/Permission'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { reservationServices } from 'src/services/sales/reservation/reservation.service'

export const PricingModal = ({ open, handleClose, titleModal, onSubmit, loadingButton, reservation }) => {
  const translate = useTranslate()
  const { cardId } = useCardId()
  const item = useRecordContext()
  const [loadingBasesList, setLoadingBasesList] = useState(false)
  const [basesList, setBasesList] = useState([])

  useEffect(() => {
    if (reservation && open) {
      getBases()
    }
  }, [reservation, open])

  const getBases = async () =>{
    setLoadingBasesList(true)
    await reservationServices.getAllAlternativeBases({
      work_unit_id: reservation.work_unit_id,
      business_unit_id: reservation.business_unit_id
    }).then(res => {
      let bases = res
      if (item?.pricing_relation?.[0]?.base_to_billing && !bases.includes(item?.pricing_relation?.[0]?.base_to_billing)) {
        bases.push(item?.pricing_relation?.[0]?.base_to_billing)
      }
      setBasesList(bases.map(base => ({ name: base })))
    }).catch(() => {
      if (item?.pricing_relation?.[0]?.base_to_billing) {
        setBasesList([{
          name: item?.pricing_relation?.[0]?.base_to_billing
        }])
      }
    }).finally(() => {
      setLoadingBasesList(false)
    })
  }

  const postSave = (data) => {
    onSubmit && onSubmit(data, cardId)
  }

  const getOptionsLiquidationByPermissions = () => {
    const options = []
    if (hasPermissionFunction('pricing', VIEW_LIQUIDATION_WHOLESALER)) {
      options.push({ key: 'liquidation_wholesaler', name: 'Liquidación mayorista' })
    }
    if (hasPermissionFunction('pricing', VIEW_LIQUIDATION_SUPPLIER)) {
      options.push({ key: 'liquidation_supplier', name: 'Liquidación proveedor' })
    }
    if (hasPermissionFunction('pricing', VIEW_LIQUIDATION_RETAIL)) {
      options.push({ key: 'liquidation_retail', name: 'Liquidación minorista' })
    }
    return options
  }

  return (
    <Dialog open={open} maxWidth='lg' fullWidth={true}>
      <Form id='post_create_form' warnWhenUnsavedChanges onSubmit={postSave}>
        <DialogTitle>
          <Grid container>
            <Grid item xs={6}>
              {titleModal}
            </Grid>

            <HasPermission permission='base' resource={'pricing'}>
              <EditInputsWrap
                className='small row'
                label={translate('ra.field.base_to_billing')}
                xs={6}
                md={6}
                lg={6}
              >
                {loadingBasesList ? 
                  <Skeleton variant='rectangular' width={'100%'} height={40} />
                :
                  <SelectInput
                    source='pricing_relation.[0].base_to_billing'
                    options={basesList}
                    label=''
                    optionsText={['name']}
                    optionValue='name'
                    showSearch
                    hideEmptyOption={true}
                    hideHelperText={true}
                    style={{ flex: 1 }}
                    disabled
                  />
                }
              </EditInputsWrap>
            </HasPermission>
          </Grid>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={3}>
            <EditInputsWrap label={translate('ra.field.additional_fare')}>
              <NumberInput
                source='pricing_relation.[0].additional_fare'
                label=''
                fullWidth
                thousandSeparator='.'
                decimalSeparator=','
                defaultValue={0}
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.comision')}>
              <NumberInput
                source='pricing_relation.[0].comision'
                label=''
                defaultValue={0}
                fullWidth
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.net_price')}>
              <NumberInput
                source='pricing_relation.[0].net_price'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.markup')}>
              <NumberInput
                source='pricing_relation.[0].markup'
                label=''
                fullWidth
                thousandSeparator='.'
                defaultValue={0}
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.fee')}>
              <NumberInput
                source='pricing_relation.[0].fee'
                label=''
                fullWidth
                thousandSeparator='.'
                defaultValue={0}
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.final_price')}>
              <NumberInput
                source='pricing_relation.[0].final_price'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.net_agency')}>
              <NumberInput
                source='pricing_relation.[0].net_agency'
                label=''
                fullWidth
                thousandSeparator='.'
                decimalSeparator=','
                defaultValue={0}
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.commission_paid')}>
              <NumberInput
                source='pricing_relation.[0].commission_paid'
                label=''
                fullWidth
                thousandSeparator='.'
                defaultValue={0}
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.commission_percentage')}>
              <NumberInput
                source='pricing_relation.[0].commission_percentage'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.expenses')}>
              <NumberInput
                source='pricing_relation.[0].expenses'
                label=''
                fullWidth
                thousandSeparator='.'
                decimalSeparator=','
                defaultValue={0}
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.fare_amount')}>
              <NumberInput
                source='pricing_relation.[0].fare_amount'
                label=''
                defaultValue={0}
                fullWidth
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.iva')}>
              <NumberInput
                source='pricing_relation.[0].iva'
                label=''
                defaultValue={0}
                fullWidth
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.over_amount')}>
              <NumberInput
                source='pricing_relation.[0].over_amount'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.taxes_amount')}>
              <NumberInput
                source='pricing_relation.[0].taxes_amount'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.num_pax')}>
              <NumberInput
                source='pricing_relation.[0].num_pax'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.num_segments')}>
              <NumberInput
                source='pricing_relation.[0].num_segments'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.additional_cost')}>
              <NumberInput
                source='pricing_relation.[0].additional_cost'
                label=''
                defaultValue={0}
                fullWidth
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.final_sale')}>
              <NumberInput
                source='pricing_relation.[0].final_sale'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.sale_discount')}>
              <NumberInput
                source='pricing_relation.[0].sale_discount'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.taxes_1')}>
              <NumberInput
                source='pricing_relation.[0].taxes_1'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.taxes_2')}>
              <NumberInput
                source='pricing_relation.[0].taxes_2'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.taxes_3')}>
              <NumberInput
                source='pricing_relation.[0].taxes_3'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.bad_practice')}>
              <NumberInput
                source='pricing_relation.[0].bad_practice'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.gateway_expenses')}>
              <NumberInput
                source='pricing_relation.[0].installment_cost'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.markup_additional')}>
              <NumberInput
                source='pricing_relation.[0].markup_additional'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={'Metasearch'}>
              <NumberInput
                source='pricing_relation.[0].metasearch'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.fraud_review')}>
              <NumberInput
                source='pricing_relation.[0].fraud_rev_cost'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.extra_cost_amnt')}>
              <NumberInput
                source='pricing_relation.[0].extra_cost_amnt'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.extra_sale_amnt')}>
              <NumberInput
                source='pricing_relation.[0].extra_sale_amnt'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>

            <HasPermission resource={ITEM_PERMISSION_ENTITY} permission={PERMISSION_SMARTBOOK}>
              <EditInputsWrap label={translate('ra.field.optmzd_mrkup_amnt')}>
                <NumberInput
                  source='pricing_relation.[0].optmzd_mrkup_amnt'
                  label=''
                  fullWidth
                  thousandSeparator='.'
                  defaultValue={0}
                  decimalSeparator=','
                  decimalScale={2}
                />
              </EditInputsWrap>
            </HasPermission>

            <EditInputsWrap label={translate('ra.field.debit')}>
              <NumberInput
                source='pricing_relation.[0].debit'
                label=''
                fullWidth
                defaultValue={0}
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
              />
            </EditInputsWrap>
          </Grid>
          <Box>
            <Typography
              variant='h6'
              color='inherit'
              sx={{
                flex: 1,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
              id='recalculate_liquidation_rule'
            >
              {translate('ra.title.recalculate_liquidation_rule')}
            </Typography>
            <Box paddingX={'10px'} width={'auto'}>
              <CheckboxLabels
                source={'pricing_relation.[0].liquidations'}
                validate={[required()]}
                choices={getOptionsLiquidationByPermissions()}
                defaultValue={getOptionsLiquidationByPermissions().map((choice) => choice.key)}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions className='dialog-actions' style={{ marginTop: '20px' }}>
          <LoadingButton
            loading={loadingButton}
            variant='contained'
            startIcon={<SaveIcon />}
            form='post_create_form'
            type='submit'
          >
            {translate('ra.action.save_and_recalculate_liquidation')}
          </LoadingButton>
          <Button onClick={handleClose} variant='outlined' color='error'>
            {translate('ra.action.cancel')}
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
